import React, { useState, useEffect } from 'react';
import { Card, CardBody } from './../../components/card/card.jsx';
import { ModalComponent } from '../expand/deviceExpandedModal.jsx';
import { useParams } from "react-router-dom";
import {
  fetchUsernameAndPhone,
  fetchUserPageDrinks,
  fetchUserPageCraftedDrinks,
  fetchUserPageMyBar,
  fetchUserRecentlyMadeDrinks,
  fetchUserMostRepeatedDrinks,
  fetchUserFavouriteDrinks,
  fetchUserDevices
} from '../../actions/users_actions.js';

import {
  fetchHWDetailsByDevice,
  fetchHWIngredientsHistory,
  fetchHardwareLastConnectedHistory,
  fetchHardwareCleaningHistory
} from '../../actions/hardware_actions.js';

import { useNavigate } from "react-router-dom";

import 'lity';
import 'lity/dist/lity.min.css';
import { useDispatch, useSelector } from 'react-redux';
import Pagination from "./../../components/pagination/Pagination.jsx";

function Profile() {
  const { userId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  var themeColor = getComputedStyle(document.body)
    .getPropertyValue("--bs-theme")
    .trim();

  const inverseColor = getComputedStyle(document.body)
    .getPropertyValue("--bs-inverse")
    .trim();

  const themeFont = getComputedStyle(document.body)
    .getPropertyValue('--bs-body-font-family')
    .trim();

  // ADD: cleaning history selectors
  const cleaningHistory = useSelector((state) => state.hardware.cleaning_history);
  const loadingCleaningHistory = useSelector((state) => state.hardware.loading_cleaning_history);
  const errorCleaningHistory = useSelector((state) => state.hardware.cleaning_history_error);

  // Devices data from the Redux store
  const userDevices = useSelector((state) => state.users.user_devices) || [];

  // Fetch device details from Redux
  const hardwareDetails = useSelector((state) => state.hardware.hw_details_info);
  const ingredientsHistoryData = useSelector((state) => state.hardware.hw_ing_history);

  // Last Connected History Data
  const lastConnectedHistory = useSelector((state) => state.hardware.hardware_last_connected_history);

  // Fetch devices when userId changes
  useEffect(() => {
    if (userId) {
      dispatch(fetchUserDevices(userId));
    }
  }, [userId, dispatch]);

  // Device type helper (NEW)
  const getDeviceType = (deviceName = "") => {
    const id = (deviceName || "").toString().toUpperCase();
    if (id.startsWith("BARSYS3") || id.startsWith("BARSYS_3") || id.startsWith("BASYS_3")) return "barsys360";
    if (id.startsWith("BARSYS_C") || id.startsWith("BASYS_C")) return "coaster";
    if (id.startsWith("BARSYS_S")) return "shakerpro";
    return "unknown";
  };

  // When devices are fetched, automatically fetch HW details for BARSYS_3 or BASYS_3 devices
  // IMPORTANT: station-related calls ONLY for 360
  useEffect(() => {
    if (!Array.isArray(userDevices?.devices) || userDevices.devices.length === 0) return;

    userDevices.devices.forEach((device) => {
      const name = device?.device_name || "";
      const type = getDeviceType(name);

      // keep this only if you truly want it prefetched; otherwise remove
      // dispatch(fetchHardwareLastConnectedHistory(name));

      if (type === "barsys360") {
        dispatch(fetchHWDetailsByDevice(name));
        dispatch(fetchHWIngredientsHistory(name));
        dispatch(fetchHardwareLastConnectedHistory(name));
      }
    });
  }, [userDevices, dispatch]);

  // Helper to convert dd/mm/yyyy -> mm/dd/yyyy
  const toUSDate = (eudate = "") => {
    const parts = eudate.split('/');
    if (parts.length === 3) {
      return `${parts[1]}/${parts[0]}/${parts[2]}`;
    }
    return eudate;
  };

  // Get username and phone from reducer
  const profile_user_info = useSelector(state => state.users.profile_user_info);
  const loading = useSelector(state => state.users.loading_profile_user);

  // Crafted Drinks from reducer
  const craftedDrinks = useSelector(state => state.users.profile_user_crafteddrinks);
  const craftedDrinksTotal = useSelector(state => state.users.profile_user_crafteddrinks_total);
  const loadingCraftedDrinks = useSelector(state => state.users.loading_profile_user_crafteddrinks);

  // Recently Made Drinks from reducer
  const recentlyMadeDrinks = useSelector(state => state.users.profile_user_recentdrinks);
  const loadingRecentlyMadeDrinks = useSelector(state => state.users.loading_profile_user_recentdrinks);
  const errorRecentlyMadeDrinks = useSelector(state => state.users.profile_user_recentdrinks_error);

  // Most Repeated Drinks
  const mostRepeatedDrinks = useSelector(state => state.users.profile_user_mostdrinks);
  const loadingMostRepeatedDrinks = useSelector(state => state.users.loading_profile_user_mostdrinks);
  const errorMostRepeatedDrinks = useSelector(state => state.users.profile_user_mostdrinks_error);

  // Favourite Drinks
  const favouriteDrinks = useSelector(state => state.users.profile_user_favdrinks);
  const loadingFavouriteDrinks = useSelector(state => state.users.loading_profile_user_favdrinks);
  const errorFavouriteDrinks = useSelector(state => state.users.profile_user_favdrinks_error);

  // Fetch Favourite Drinks on Mount
  useEffect(() => {
    if (userId) {
      dispatch(fetchUserFavouriteDrinks(userId));
    }
  }, [userId, dispatch]);

  // Fetch Most Repeated Drinks on Mount
  useEffect(() => {
    if (userId) {
      dispatch(fetchUserMostRepeatedDrinks(userId));
    }
  }, [userId, dispatch]);

  // Fetch Recently Made Drinks on Mount
  useEffect(() => {
    if (userId) {
      dispatch(fetchUserRecentlyMadeDrinks(userId));
    }
  }, [userId, dispatch]);

  // Fetch user info on mount
  useEffect(() => {
    if (userId) {
      dispatch(fetchUsernameAndPhone(userId));
    }
  }, [userId, dispatch]);

  // Fetch Crafted Drinks on mount
  useEffect(() => {
    if (userId) {
      dispatch(fetchUserPageCraftedDrinks(userId));
    }
  }, [userId, dispatch]);

  // Fallback to N/A if missing
  const username = profile_user_info?.username?.length > 3
    ? profile_user_info.username
    : "N/A";
  const phone = profile_user_info?.phone_number?.length > 3
    ? profile_user_info.phone_number
    : "N/A";

  const mydrinksloading = useSelector(state => state.users.loading_profile_user_drinks);
  const drinks = useSelector(state => state.users.profile_user_drinks);
  const totalDrinks = useSelector(state => state.users.profile_user_drinks_total);

  const myBarLoading = useSelector(state => state.users.loading_profile_user_mybar);
  const myBarIngredients = useSelector(state => state.users.profile_user_mybar) ?? [];
  const myBarTotal = useSelector(state => state.users.profile_user_mybar_total);

  useEffect(() => {
    if (userId) dispatch(fetchUserPageDrinks(userId));
  }, [userId, dispatch]);

  // My Bar Fetch on Mount
  useEffect(() => {
    if (userId) {
      dispatch(fetchUserPageMyBar(userId));
    }
  }, [userId, dispatch]);

  // === Build modal-compatible lastCleanedData (all rows, keep duplicates, UTC) ===
  const stationLetters = ['A', 'B', 'C', 'D', 'E', 'F'];
  const USE_UTC = true; // <-- shows exact times from API (UTC "Z")

  const numToLetter = (n) => {
    const idx = Number(n);
    return Number.isFinite(idx) && idx >= 1 && idx <= stationLetters.length
      ? stationLetters[idx - 1]
      : null;
  };

  const toDDMMYYYY_HHMM = (iso, useUTC = USE_UTC) => {
    if (!iso) return { date: '-', time: '-' };
    const d = new Date(iso);
    if (isNaN(d.getTime())) return { date: '-', time: '-' };
    const dd = String(useUTC ? d.getUTCDate() : d.getDate()).padStart(2, '0');
    const mm = String((useUTC ? d.getUTCMonth() : d.getMonth()) + 1).padStart(2, '0');
    const yyyy = useUTC ? d.getUTCFullYear() : d.getFullYear();
    const hh = String(useUTC ? d.getUTCHours() : d.getHours()).padStart(2, '0');
    const min = String(useUTC ? d.getUTCMinutes() : d.getMinutes()).padStart(2, '0');
    return { date: `${dd}/${mm}/${yyyy}`, time: `${hh}:${min}` };
  };

  const buildLastCleanedData = (history) => {
    // Normalize reducer payload (supports raw array or { success, data: [...] })
    const src = Array.isArray(history)
      ? history
      : (history && Array.isArray(history.data) ? history.data : []);

    if (!src || !src.length) return [];

    // Collect ALL dates per station (keep duplicates). Sort desc so newest is first.
    const byStation = { A: [], B: [], C: [], D: [], E: [], F: [] };
    src.forEach(row => {
      const letter = numToLetter(row.station);
      if (!letter) return;
      const arr = Array.isArray(row.dates) ? row.dates.slice() : [];
      arr.sort((a, b) => new Date(b) - new Date(a)); // newest → oldest
      byStation[letter] = arr;
    });

    // Build one row per index up to the longest list
    const maxLen = Math.max(0, ...Object.values(byStation).map(arr => arr.length));
    const rows = [];
    for (let i = 0; i < maxLen; i++) {
      const snap = {};
      stationLetters.forEach(letter => {
        const iso = byStation[letter][i];
        const { date, time } = toDDMMYYYY_HHMM(iso);
        snap[`Station_${letter}`] = { date, time };
      });
      rows.push(snap);
    }
    return rows;
  };

  const lastCleanedData = buildLastCleanedData(cleaningHistory);

  const requestCleaningHistory = React.useCallback((deviceName) => {
    dispatch(fetchHardwareCleaningHistory(deviceName));
  }, [dispatch]);

  const [activeTab, setActiveTab] = useState('my-drinks');

  const [barCurrentPage, setBarCurrentPage] = useState(1);
  const barItemsPerPage = 10;

  const paginatedBarIngredients = myBarIngredients.slice(
    (barCurrentPage - 1) * barItemsPerPage,
    barCurrentPage * barItemsPerPage
  );

  const startIdx = myBarTotal === 0 ? 0 : (barCurrentPage - 1) * barItemsPerPage + 1;
  const endIdx = Math.min(barCurrentPage * barItemsPerPage, myBarTotal);

  return (
    <Card>
      <CardBody className="p-0">
        <div className="profile">
          <div className="profile-container">
            <div className="profile-sidebar">
              <div className="desktop-sticky-top">
                <div
                  style={{
                    width: "180px",
                    height: "180px",
                    borderRadius: "12px",
                    overflow: "hidden",
                    background: "#b0bec5",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 28px auto",
                    border: "1px solid #b0bec5",
                  }}
                >
                  <img
                    src="/assets/img/usershimmer.png"
                    alt="User"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                      borderRadius: "12px",
                      background: "#eceff1",
                    }}
                    onError={e => {
                      // Prevent infinite loop
                      if (!e.target.src.endsWith('usershimmer.png')) {
                        e.target.src = "/assets/img/usershimmer.png";
                      }
                    }}
                  />
                </div>

                <h4>{loading ? 'Loading...' : username}</h4>
                <div className="mb-3 text-inverse text-opacity-50 fw-bold mt-n2">{loading ? 'Loading...' : phone}</div>

                <div className="mb-3 text-inverse text-opacity-50 fw-bold mt-n2">@{loading ? 'Loading...' : username.toLowerCase().replace(/\s+/g, '')}</div>

                <hr className="mt-4 mb-4" />

                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div className="fw-bold fs-16px">User Devices</div>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-theme"
                    onClick={() =>
                      // fire the Bootstrap tab toggle for #my-devices:
                      document.querySelector('a[href="#my-devices"][data-bs-toggle="tab"]').click()
                    }
                  >
                    View
                  </button>
                </div>

                {/* Dynamically displaying the first 3 devices */}
                {userDevices?.devices?.slice(0, 3).map((device, index) => {
                  const type = getDeviceType(device.device_name);

                  const deviceImage =
                    type === "barsys360" ? "/assets/img/barsys360.jpeg" :
                      type === "coaster" ? "/assets/img/BarsysCoaster.jpg" :
                        type === "shakerpro" ? "/assets/img/Shaker-Pro.jpg" :
                          "/assets/img/glassshimmer.png";

                  const deviceLabel =
                    type === "barsys360" ? "BARSYS 360" :
                      type === "coaster" ? "BARSYS Coaster" :
                        type === "shakerpro" ? "BARSYS Shaker Pro" :
                          "BARSYS Device";

                  return (
                    <div key={index} className="d-flex align-items-center mb-3">
                      <img
                        src={deviceImage}
                        alt={deviceLabel}
                        width="30"
                        className="rounded-circle"
                        style={{
                          width: 48,
                          height: 48,
                          borderRadius: '50%',
                          objectFit: 'cover',
                        }}
                        onError={e => {
                          e.target.onerror = null;
                          e.target.src = "/assets/img/glassshimmer.png";
                        }}
                      />
                      <div className="flex-fill px-3">
                        {/* Display device type first */}
                        <div className="fs-12px text-inverse text-opacity-50">
                          {deviceLabel}
                        </div>

                        {/* Display device name */}
                        <div className="fw-bold text-truncate w-100px" title={device.device_name}>
                          {device.device_name}
                        </div>
                      </div>
                    </div>
                  );
                })}

              </div>
            </div>

            <div className="profile-content">
              <ul className="profile-tab nav nav-tabs nav-tabs-v2">
                <li className="nav-item">
                  <a
                    href="#my-drinks"
                    className={`nav-link ${activeTab === 'my-drinks' ? 'active' : ''}`}
                    data-bs-toggle="tab"
                    onClick={() => setActiveTab('my-drinks')}
                  >
                    <div className="nav-field">My Drinks</div>
                    <div className="nav-value">{totalDrinks}</div>
                  </a>
                </li>
                <li className="nav-item">
                  <a
                    href="#my-devices"
                    className={`nav-link ${activeTab === 'my-devices' ? 'active' : ''}`}
                    data-bs-toggle="tab"
                    onClick={() => setActiveTab('my-devices')}
                  >
                    <div className="nav-field">Devices</div>
                    <div className="nav-value">{userDevices.devices ? userDevices.devices.length : 0}</div>

                  </a>
                </li>
                <li className="nav-item">
                  <a
                    href="#crafted-drinks"
                    className={`nav-link ${activeTab === 'crafted-drinks' ? 'active' : ''}`}
                    data-bs-toggle="tab"
                    onClick={() => setActiveTab('crafted-drinks')}
                  >
                    <div className="nav-field">Crafted Drinks</div>
                    <div className="nav-value">{craftedDrinksTotal}</div>
                  </a>
                </li>
                <li className="nav-item">
                  <a
                    href="#my-bar"
                    className={`nav-link ${activeTab === 'my-bar' ? 'active' : ''}`}
                    data-bs-toggle="tab"
                    onClick={() => setActiveTab('my-bar')}
                  >
                    <div className="nav-field">My Bar</div>
                    <div className="nav-value">{myBarTotal}</div>
                  </a>
                </li>
              </ul>

              <div className="profile-content-container">
                <div className="row gx-4">
                  <div className="col-xl-8">
                    <div className="tab-content p-0">
                      <div className={`tab-pane fade${activeTab === 'my-drinks' ? ' show active' : ''}`} id="my-drinks">
                        <Card>
                          <CardBody>
                            {drinks && drinks.length > 0 ? (
                              drinks.map((c, idx) => (
                                <div
                                  key={c.recipe_id}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    border: '1px solid gray',
                                    borderRadius: '8px',
                                    padding: '16px',
                                    marginBottom: '5px',
                                  }}
                                >
                                  {/* Row Number */}
                                  <div style={{ width: '44px', textAlign: 'center', fontWeight: 'bold', color: themeColor, paddingRight: '20px' }}>
                                    {idx + 1}
                                  </div>
                                  {/* Image */}
                                  <img
                                    src={typeof c.img === "string" && c.img.trim() ? c.img : "/assets/img/glassshimmer.png"}
                                    alt={c.name}
                                    style={{ width: "80px", height: "80px", objectFit: "cover", borderRadius: "8px" }}
                                    onError={e => {
                                      // Prevent infinite loop if shimmer image also fails
                                      if (!e.target.src.endsWith('glassshimmer.png')) {
                                        e.target.src = '/assets/img/glassshimmer.png';
                                      }
                                    }}
                                  />

                                  {/* Name */}
                                  <div style={{ flex: 1, marginLeft: '16px' }}>
                                    <h5 className='text-inverse text-opacity-70' style={{ margin: 0, fontSize: '18px', textAlign: 'left' }}>{c.name}</h5>
                                  </div>

                                  {/* Ingredients */}
                                  <div style={{ flex: 1, marginLeft: '16px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                    <div><strong style={{ color: themeColor }}>Base:</strong> {c.base}</div>
                                    <div><strong style={{ color: themeColor }}>Mixer:</strong> {c.mixer}</div>
                                    <div><strong style={{ color: themeColor }}>Garnish:</strong> {c.garnish}</div>
                                  </div>
                                </div>
                              ))
                            ) : mydrinksloading ? (
                              <div>Loading drinks...</div>
                            ) : (
                              <div>No drinks found.</div>
                            )}
                          </CardBody>
                        </Card>
                      </div>


                      <div className={`tab-pane fade${activeTab === 'my-devices' ? ' show active' : ''}`} id="my-devices">
                        <div className="tab-pane active" id="devices">
                          {Array.isArray(userDevices.devices) && userDevices.devices.length > 0 ? (
                            userDevices.devices.map((device, index) => {
                              const type = getDeviceType(device.device_name);
                              const isBarsys360 = type === "barsys360";

                              // Determine the image to show based on the device name / prefix
                              const deviceImage =
                                type === "barsys360" ? "/assets/img/barsys360.jpeg" :
                                  type === "coaster" ? "/assets/img/BarsysCoaster.jpg" :
                                    type === "shakerpro" ? "/assets/img/Shaker-Pro.jpg" :
                                      "/assets/img/glassshimmer.png";

                              return (
                                <Card key={index} className="mb-3 bg-transparent text-light border-secondary">
                                  <CardBody className="d-flex position-relative">

                                    {/* Expanded modal trigger ONLY for 360 */}
                                    {isBarsys360 && (
                                      <div
                                        className="d-flex fw-bold small mb-3 align-items-center"
                                        style={{
                                          position: 'absolute',
                                          top: '16px',
                                          right: '16px',
                                          cursor: 'pointer'
                                        }}

                                      >
                                        <ModalComponent
                                          lastConnectedData={lastConnectedHistory}
                                          lastCleanedData={lastCleanedData}
                                          ingredientsHistoryData={ingredientsHistoryData}
                                          deviceName={device.device_name}
                                          onShown={() => requestCleaningHistory(device.device_name)}
                                        />
                                      </div>
                                    )}

                                    {/* Left column: Device name above a larger image, both left-aligned */}
                                    <div
                                      style={{
                                        width: '150px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'flex-start',
                                        textAlign: 'left',
                                        marginRight: '2rem',
                                        alignSelf: 'flex-start'
                                      }}
                                    >
                                      <h6
                                        className="text-inverse text-opacity-60"
                                        style={{ textTransform: 'uppercase', color: '#ffffff', marginBottom: '1.5rem' }}
                                      >
                                        <span
                                          style={{ cursor: 'pointer' }}
                                          onClick={() => navigate(`/hardware-profile/${device.device_name}`)}
                                        >
                                          {device.device_name}
                                        </span>
                                      </h6>
                                      <img
                                        src={deviceImage}
                                        alt={device.device_name}
                                        style={{
                                          width: '140px',
                                          height: '140px',
                                          borderRadius: '8px',
                                          objectFit: 'cover',
                                          margin: 0
                                        }}
                                        onError={e => {
                                          e.target.onerror = null;
                                          e.target.src = "/assets/img/glassshimmer.png";
                                        }}
                                      />
                                    </div>

                                    {/* Right column: Details */}
                                    <div style={{ flex: 1 }}>
                                      {/* Last Connected Time and Date */}
                                      <p className="mb-3 text-inverse" style={{ paddingLeft: 100 }}>
                                        <strong> Last Connected:</strong>{" "}
                                        {device?.last_activity
                                          ? new Date(device.last_activity * 1000).toLocaleString()
                                          : "--"}
                                      </p>

                                      {/* Station Setup ONLY for 360 */}
                                      {isBarsys360 && hardwareDetails && hardwareDetails.stations && (
                                        <div
                                          style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'flex-start',
                                            marginTop: '0.5rem',
                                            paddingRight: '2rem'
                                          }}
                                        >
                                          {/* Station Setup List: Dynamic Information */}
                                          <div style={{ display: 'flex', flexDirection: 'column', marginTop: '10px' }}>
                                            <strong style={{ color: inverseColor }}>Station Setup:</strong>
                                            <div style={{ marginTop: '0.25rem' }}>
                                              {hardwareDetails.stations.map((station) => (
                                                <div key={station.station} style={{ color: themeColor }}>
                                                  {`Station ${station.station}: `}
                                                  <span style={{ color: inverseColor, fontWeight: 'bold' }}>
                                                    {station.ingredient_name}
                                                  </span>
                                                </div>
                                              ))}
                                            </div>
                                          </div>

                                          {/* Last Cleaned Info (still handled in modal) */}
                                          <div style={{ textAlign: 'left' }}>
                                            {/* intentionally blank in card view */}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  </CardBody>
                                </Card>
                              );
                            })
                          ) : (
                            <div>No devices found.</div>
                          )}
                        </div>
                      </div>


                      <div className={`tab-pane fade${activeTab === 'crafted-drinks' ? ' show active' : ''}`} id="crafted-drinks">
                        <Card>
                          <CardBody>
                            {loadingCraftedDrinks ? (
                              <div>Loading crafted drinks...</div>
                            ) : (craftedDrinks && craftedDrinks.length > 0 ? (
                              craftedDrinks.map((c, idx) => (
                                <div
                                  key={c.make_id || c.recipe_id || idx}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    border: '1px solid gray',
                                    borderRadius: '8px',
                                    padding: '16px',
                                    marginBottom: '5px',
                                  }}
                                >
                                  {/* Row Number */}
                                  <div style={{ width: '44px', textAlign: 'center', fontWeight: 'bold', color: themeColor, paddingRight: '20px' }}>
                                    {idx + 1}
                                  </div>
                                  {/* Image */}
                                  <img
                                    src={c.img}
                                    alt={c.name}
                                    style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px' }}
                                    onError={e => { e.target.src = '/assets/img/glassshimmer.png'; }}
                                  />
                                  {/* Name */}
                                  <div style={{ flex: 1, marginLeft: '16px' }}>
                                    <h5 className='text-inverse text-opacity-70' style={{ margin: 0, fontSize: '18px', textAlign: 'left' }}>{c.name}</h5>
                                  </div>
                                  {/* Ingredients */}
                                  <div style={{ flex: 1, marginLeft: '16px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                    <div><strong style={{ color: themeColor }}>Base:</strong> {c.base}</div>
                                    <div><strong style={{ color: themeColor }}>Mixer:</strong> {c.mixer}</div>
                                    <div><strong style={{ color: themeColor }}>Garnish:</strong> {c.garnish}</div>
                                  </div>
                                  {/* Optional: show date/time */}
                                  <div style={{ minWidth: 100, textAlign: 'right', color: inverseColor }}>
                                    {c.addedDay && c.addedMonth && c.addedYear && c.addedTime &&
                                      <div style={{ fontSize: 12 }}>
                                        <div>{String(c.addedDay).padStart(2, '0')}/{String(c.addedMonth).padStart(2, '0')}/{c.addedYear}</div>
                                        <div>{c.addedTime}</div>
                                      </div>
                                    }
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div>No crafted drinks found.</div>
                            ))}
                          </CardBody>
                        </Card>
                      </div>

                      <div className={`tab-pane fade${activeTab === 'my-bar' ? ' show active' : ''}`} id="my-bar">
                        <Card>
                          <CardBody>
                            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "10px" }}>
                              <span style={{ color: "#aaa", fontSize: 13 }}>
                                Displaying{' '}
                                <span style={{ color: themeColor }}>{startIdx}</span>
                                –
                                <span style={{ color: themeColor }}>{endIdx}</span>
                                {' '}of{' '}
                                <span style={{ color: themeColor }}>{myBarTotal}</span>
                              </span>
                            </div>

                            {myBarLoading ? (
                              <div>Loading ingredients...</div>
                            ) : paginatedBarIngredients.length === 0 ? (
                              <div>No ingredients in My Bar.</div>
                            ) : (
                              paginatedBarIngredients.map((item, idx) => (
                                <div
                                  key={`${item.ingredient_id}-${item.type}`}
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    border: '1px solid rgba(255,255,255,0.2)',
                                    borderRadius: '8px',
                                    padding: '16px',
                                    marginBottom: '5px'
                                  }}
                                >
                                  {/* Row Number */}
                                  <div
                                    style={{
                                      width: '50px',
                                      textAlign: 'center',
                                      fontWeight: 'bold',
                                      color: themeColor,
                                      paddingRight: '20px'
                                    }}
                                  >
                                    {(barCurrentPage - 1) * barItemsPerPage + idx + 1}
                                  </div>
                                  {/* Ingredient Name and Type */}
                                  <div style={{ flex: 2, marginRight: '1rem' }}>
                                    <div
                                      className="text-inverse text-opacity-80"
                                      style={{ margin: 0, fontSize: '16px' }}
                                    >
                                      {item.ingredient_name}
                                    </div>
                                    <small style={{ color: inverseColor }}>{item.type}</small>
                                  </div>
                                  {/* Date & Time - now just right of the ingredient name */}
                                  <div style={{ flex: 1, minWidth: '130px', textAlign: 'left', color: '#888', fontSize: 13 }}>
                                    <div>
                                      <span style={{ marginRight: 4, color: themeColor }}>Date:</span>
                                      <span style={{ color: inverseColor }}>{toUSDate(item.created_date) || 'N/A'}</span>
                                    </div>
                                    <div>
                                      <span style={{ marginRight: 4, color: themeColor }}>Time:</span>
                                      <span style={{ color: inverseColor }}>{item.created_time || 'N/A'}</span>
                                    </div>

                                  </div>
                                </div>
                              ))
                            )}
                            <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "10px", marginTop: "10px" }}>
                              <Pagination
                                currentPage={barCurrentPage}
                                itemsPerPage={barItemsPerPage}
                                totalItems={myBarTotal}
                                paginate={setBarCurrentPage}
                              />
                            </div>
                          </CardBody>
                        </Card>
                      </div>
                    </div>
                  </div>
                  <div className="col-xl-4">
                    <div className="desktop-sticky-top d-none d-lg-block">
                      <Card className="mb-3">
                        <div className="list-group list-group-flush">
                          <div className="list-group-item fw-bold px-3 d-flex">
                            <span className="flex-fill">Updates/Activities</span>
                          </div>
                          <div className="list-group-item px-3">
                            <div className="text-inverse text-opacity-80" style={{ paddingBottom: "10px" }}>
                              <medium><strong>Recently Made Drinks</strong></medium>
                            </div>
                            <CardBody className="p-0">
                              <div className="row gx-3 gy-2">
                                {loadingRecentlyMadeDrinks ? (
                                  <div className="col-12 py-3 text-center text-muted">Loading...</div>
                                ) : errorRecentlyMadeDrinks ? (
                                  <div className="col-12 py-3 text-danger">{errorRecentlyMadeDrinks}</div>
                                ) : recentlyMadeDrinks && recentlyMadeDrinks.length > 0 ? (
                                  recentlyMadeDrinks.slice(0, 6).map((drink, idx) => (
                                    <div key={drink.make_id || idx} className="col-12 col-md-6">
                                      <div
                                        style={{
                                          width: "100%",
                                          aspectRatio: "1 / 1",
                                          borderRadius: "10px",
                                          background: "#f8f8f8",
                                          overflow: "hidden",
                                          marginBottom: 6,
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                        }}
                                      >
                                        <img
                                          src={drink.img || '/assets/img/glassshimmer.png'}
                                          alt={drink.name}
                                          style={{
                                            width: "100%",
                                            height: "100%",
                                            objectFit: "cover",
                                            display: "block",
                                            borderRadius: "8px"
                                          }}
                                          onError={e => {
                                            if (!e.target.src.endsWith('glassshimmer.png')) {
                                              e.target.src = '/assets/img/glassshimmer.png';
                                            }
                                          }}
                                        />
                                      </div>
                                      <div>
                                        <small
                                          className="text-inverse text-opacity-80"
                                          style={{
                                            paddingLeft: 2,
                                            fontWeight: 500,
                                            display: "block",
                                            fontSize: 13,
                                            textOverflow: "ellipsis",
                                            overflow: "hidden",
                                            whiteSpace: "nowrap"
                                          }}
                                          title={drink.name}
                                        >
                                          {drink.name}
                                        </small>
                                      </div>
                                    </div>
                                  ))
                                ) : (
                                  <div className="col-12 py-3 text-muted">No drinks found.</div>
                                )}
                              </div>
                            </CardBody>
                          </div>

                          <CardBody className="p-3">
                            <div className="mb-3">
                              <div className="text-inverse text-opacity-80">
                                <medium>
                                  <strong>Most Repeated Drinks</strong>
                                </medium>
                              </div>
                            </div>

                            <div className="row gx-2 gy-2">
                              {loadingMostRepeatedDrinks ? (
                                <div className="col-12 py-3 text-center text-muted">Loading...</div>
                              ) : errorMostRepeatedDrinks ? (
                                <div className="col-12 py-3 text-danger">{errorMostRepeatedDrinks}</div>
                              ) : mostRepeatedDrinks && mostRepeatedDrinks.length > 0 ? (
                                mostRepeatedDrinks.slice(0, 4).map((drink, idx) => (
                                  <div key={drink.recipe_id || idx} className="col-6 col-md-3 text-left">
                                    <div
                                      style={{
                                        width: "100%",
                                        aspectRatio: "1 / 1",
                                        borderRadius: "8px",
                                        background: "#f5f6fa",
                                        overflow: "hidden",
                                        marginBottom: 5,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                      }}
                                    >
                                      <img
                                        src={drink.img && drink.img.trim() ? drink.img : '/assets/img/usershimmer.png'}
                                        alt={drink.name}
                                        style={{
                                          width: "100%",
                                          height: "100%",
                                          objectFit: "cover",
                                          display: "block",
                                          borderRadius: "8px"
                                        }}
                                        onError={e => {
                                          if (!e.target.src.endsWith('glassshimmer.png')) {
                                            e.target.src = '/assets/img/glassshimmer.png';
                                          }
                                        }}
                                      />
                                    </div>
                                    <div>
                                      <small
                                        className="text-inverse text-opacity-80"
                                        style={{
                                          paddingLeft: 2,
                                          fontWeight: 500,
                                          fontSize: 12,
                                          display: "block",
                                          textOverflow: "ellipsis",
                                          overflow: "hidden",
                                          whiteSpace: "nowrap"
                                        }}
                                        title={drink.name}
                                      >
                                        {drink.name}
                                      </small>
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <div className="col-12 py-3 text-muted">No drinks found.</div>
                              )}
                            </div>
                          </CardBody>
                        </div>

                        <div className="list-group list-group-flush">
                          <div className="list-group-item px-3"></div>
                          <CardBody className="p-3">
                            <div className="mb-3">
                              <div className="text-inverse text-opacity-80">
                                <medium>
                                  <strong>Favourites</strong>
                                </medium>
                              </div>
                            </div>

                            <div className="row gx-2 gy-2">
                              {loadingFavouriteDrinks ? (
                                <div className="col-12 py-3 text-center text-muted">Loading...</div>
                              ) : errorFavouriteDrinks ? (
                                <div className="col-12 py-3 text-danger">{errorFavouriteDrinks}</div>
                              ) : favouriteDrinks && favouriteDrinks.length > 0 ? (
                                favouriteDrinks.slice(0, 4).map((drink, idx) => (
                                  <div key={drink.recipe_id || idx} className="col-6 col-md-3 text-left">
                                    <div
                                      style={{
                                        width: "100%",
                                        aspectRatio: "1 / 1",
                                        borderRadius: "8px",
                                        background: "#f5f6fa",
                                        overflow: "hidden",
                                        marginBottom: 5,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                      }}
                                    >
                                      <img
                                        src={drink.img && drink.img.trim() ? drink.img : '/assets/img/usershimmer.png'}
                                        alt={drink.name}
                                        style={{
                                          width: "100%",
                                          height: "100%",
                                          objectFit: "cover",
                                          display: "block",
                                          borderRadius: "8px"
                                        }}
                                        onError={e => {
                                          if (!e.target.src.endsWith('glassshimmer.png')) {
                                            e.target.src = '/assets/img/glassshimmer.png';
                                          }
                                        }}
                                      />

                                    </div>
                                    <div>
                                      <small
                                        className="text-inverse text-opacity-80"
                                        style={{
                                          paddingLeft: 2,
                                          fontWeight: 500,
                                          fontSize: 12,
                                          display: "block",
                                          textOverflow: "ellipsis",
                                          overflow: "hidden",
                                          whiteSpace: "nowrap"
                                        }}
                                        title={drink.name}
                                      >
                                        {drink.name}
                                      </small>
                                    </div>
                                  </div>
                                ))
                              ) : (
                                <div className="col-12 py-3 text-muted">No favourites found.</div>
                              )}
                            </div>
                          </CardBody>
                        </div>
                      </Card>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  )
}

export default Profile;
